/**
 * Entry points for the contract questions: the suggested questions for one
 * agreement (Nami's chips on its page), the repository-wide ones (Nami's chips
 * on the contracts list) and the documents a free-text question on an
 * agreement's page is answered from. Agreements without authored answers get
 * suggestions built from their extracted clauses.
 */
import type { ScriptedAnswer } from '../../ask/types';
import { clausesForContract } from '../../data/clauses';
import type { Clause, ClauseKind } from '../../data/clauses';
import { getContract } from '../../data/contracts';
import { getSupplier } from '../../data/suppliers';
import type { ContractId } from '../../data/types';
import { cite, shortTitleFor } from './ask-documents';
import { AUTHORED_QA } from './qa-authored';
import { buildRepositorySuggestions } from './qa-repository';

export { allAskDocuments, askDocumentsForContract } from './ask-documents';

function firstClause(
  clauses: Clause[],
  kinds: ClauseKind[],
): Clause | undefined {
  return clauses.find((clause) => kinds.includes(clause.kind));
}

function answerFromClause(
  contractId: ContractId,
  clause: Clause,
): Pick<ScriptedAnswer, 'answer' | 'citations'> {
  const note = clause.riskNote ? `\n\n${clause.riskNote}` : '';
  return {
    answer: `**${clause.value}.** ${clause.detail} (§${clause.section})${note}`,
    citations: [cite(contractId, clause.section, clause.quote)],
  };
}

/** Suggestions built from the clauses of an agreement with no authored script. */
function genericSuggestions(contractId: ContractId): ScriptedAnswer[] {
  const contract = getContract(contractId);
  const supplier = getSupplier(contract.supplierId);
  const clauses = clausesForContract(contractId);
  const suggestions: ScriptedAnswer[] = [];

  const payment = firstClause(clauses, ['payment_terms']);
  if (payment) {
    suggestions.push({
      question: 'What are the payment terms?',
      aliases: [
        `What are the payment terms with ${supplier.shortName}?`,
        `When do we pay ${supplier.shortName}?`,
      ],
      thinking: [
        `Reading ${shortTitleFor(contractId)}`,
        'Finding the payment terms clause',
      ],
      ...answerFromClause(contractId, payment),
    });
  }

  const exit = firstClause(clauses, ['auto_renewal', 'termination']);
  if (exit) {
    suggestions.push({
      question: 'How does the agreement renew or end?',
      aliases: [
        'Can we terminate for convenience?',
        'What are the termination rights?',
        'Does the agreement auto-renew?',
      ],
      thinking: [
        `Reading ${shortTitleFor(contractId)}`,
        'Finding the term, renewal and termination clauses',
      ],
      ...answerFromClause(contractId, exit),
    });
  }

  const delivery = firstClause(clauses, ['incoterms', 'pricing']);
  if (delivery && delivery !== payment && delivery !== exit) {
    suggestions.push({
      question: 'What is the price and delivery basis?',
      aliases: ['What does the price include?', 'Who pays freight?'],
      thinking: [
        `Reading ${shortTitleFor(contractId)}`,
        'Finding the pricing and delivery clauses',
      ],
      ...answerFromClause(contractId, delivery),
    });
  }

  if (suggestions.length === 0) {
    suggestions.push({
      question: 'What does this agreement cover?',
      aliases: ['Summarize this agreement'],
      thinking: [`Reading ${shortTitleFor(contractId)}`],
      answer: clauses
        .slice(0, 2)
        .map(
          (clause) =>
            `- **${clause.label}:** ${clause.value}. ${clause.detail} (§${clause.section})`,
        )
        .join('\n'),
      citations: clauses
        .slice(0, 2)
        .map((clause) => cite(contractId, clause.section, clause.quote)),
    });
  }
  return suggestions;
}

/** Suggested questions for one agreement, answered in Nami on its page. */
export function suggestionsFor(contractId: ContractId): ScriptedAnswer[] {
  return AUTHORED_QA[contractId] ?? genericSuggestions(contractId);
}

/** Suggested questions across every agreement, answered in Nami on the contracts list. */
export function repositorySuggestions(): ScriptedAnswer[] {
  return buildRepositorySuggestions();
}
